// src/components/institution/modals/StaffDetailsModal.jsx
import React from 'react';
import { X, Mail, Phone, MapPin, Calendar, Award, BookOpen, Users, Clock } from 'lucide-react';

export const StaffDetailsModal = ({ staff, onClose, styling }) => {
  if (!staff) return null;

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Award className="h-5 w-5" />;
      case 'educator': return <BookOpen className="h-5 w-5" />;
      case 'support': return <Users className="h-5 w-5" />;
      default: return <Users className="h-5 w-5" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700 border-red-200';
      case 'educator': return 'bg-green-100 text-green-700 border-green-200';
      case 'support': return 'bg-purple-100 text-purple-700 border-purple-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center rounded-t-xl">
          <h2 className="text-xl font-bold text-gray-900">Staff Profile</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Profile Section */}
          <div className="flex items-start gap-6">
            <div className="flex-shrink-0">
              {staff.profileImage || staff.photo ? (
                <img
                  src={staff.profileImage || staff.photo}
                  alt={staff.name}
                  className="w-24 h-24 rounded-full object-cover border-4 border-gray-100"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center border-4 border-gray-100">
                  <Users className="h-8 w-8 text-gray-500" />
                </div>
              )}
            </div>

            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{staff.name}</h1>
              <p className="text-xl text-blue-600 font-semibold mb-2">{staff.position}</p>
              
              {staff.department && (
                <p className="text-gray-600 mb-3">{staff.department}</p>
              )}

              {/* Role Badge */}
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${getRoleColor(staff.role)} font-medium text-sm`}>
                {getRoleIcon(staff.role)}
                {staff.role === 'admin' ? 'Administrator' : 
                 staff.role === 'educator' ? 'Educator' : 'Support Staff'}
              </div>
            </div>
          </div>

          {/* Bio */}
          {staff.bio && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">About</h3>
              <p className="text-gray-700 leading-relaxed">{staff.bio}</p>
            </div>
          )}

          {/* Qualifications */}
          {staff.qualifications && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Award className="h-5 w-5 text-blue-600" />
                Qualifications
              </h3>
              <p className="text-gray-700">{staff.qualifications}</p>
            </div>
          )}

          {/* Subjects & Grades for Educators */}
          {staff.role === 'educator' && (staff.subjects?.length > 0 || staff.grades?.length > 0) && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Subjects */}
              {staff.subjects?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <BookOpen className="h-5 w-5 text-green-600" />
                    Subjects
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {staff.subjects.map(subject => (
                      <span key={subject} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                        {subject}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Grades */}
              {staff.grades?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    Grade Levels
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {staff.grades.map(grade => (
                      <span key={grade} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                        {grade}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h3>
            <div className="space-y-3">
              {staff.email && (
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <a
                    href={`mailto:${staff.email}`}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {staff.email}
                  </a>
                </div>
              )}

              {staff.phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-gray-400" />
                  <a
                    href={`tel:${staff.phone}`}
                    className="text-blue-600 hover:text-blue-800 font-medium"
                  >
                    {staff.phone}
                  </a>
                </div>
              )}

              {staff.officeLocation && (
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-700">{staff.officeLocation}</span>
                </div>
              )}

              {staff.officeHours && (
                <div className="flex items-center gap-3">
                  <Clock className="h-5 w-5 text-gray-400" />
                  <span className="text-gray-700">{staff.officeHours}</span>
                </div>
              )}
            </div>
          </div>

          {/* Additional Information */}
          {staff.startDate && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                Employment
              </h3>
              <p className="text-gray-700">
                Started: {new Date(staff.startDate).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};